import { useEffect, useState } from "react";

import { Add, ArrowBack, Translate } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import {
  SurveyGroupResponse,
  SurveyConfiguration,
  getOrgSurveyGroups,
  deleteOrgSurveyGroup,
  getOrgSurveyConfigPortal,
  getSurveyTranslations,
  patchOrgSurveyGroup,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import {
  isEnglishCode,
  languageCodeToTranslateLocale,
} from "@/common/constants/languages";

import CreateGroupDialog from "./CreateGroupDialog";
import SurveyGroupCard from "./SurveyGroupCard";
import styles from "./SurveyGroupManagement.module.scss";

const SurveyGroupManagement: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const app = location.pathname.includes("rubiintel")
    ? "rubiintel"
    : "rubireportv2";

  const [groups, setGroups] = useState<SurveyGroupResponse[]>([]);
  const [configs, setConfigs] = useState<SurveyConfiguration[]>([]);
  const [supportedLanguages, setSupportedLanguages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteConfirmGroup, setDeleteConfirmGroup] =
    useState<SurveyGroupResponse | null>(null);
  const [bulkTranslating, setBulkTranslating] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [groupsRes, configsRes] = await Promise.all([
          getOrgSurveyGroups({ path: { org_id: orgId ?? "" } }),
          getOrgSurveyConfigPortal({ path: { org_id: orgId ?? "" } }),
        ]);
        setGroups(groupsRes.data!.groups);
        setConfigs(configsRes.data!.surveys);
        setSupportedLanguages(configsRes.data?.supported_languages ?? []);
      } catch {
        navigate("/");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orgId, navigate]);

  const handleCreated = (group: SurveyGroupResponse) => {
    setGroups((prev) => [...prev, group]);
    toast.success("Group created");
  };

  const handleDelete = async () => {
    if (!deleteConfirmGroup) return;
    try {
      await deleteOrgSurveyGroup({
        path: { org_id: orgId ?? "", group_id: deleteConfirmGroup.id },
      });
      setGroups((prev) => prev.filter((g) => g.id !== deleteConfirmGroup.id));
      toast.success("Group deleted");
    } catch {
      toast.error("Failed to delete group");
    } finally {
      setDeleteConfirmGroup(null);
    }
  };

  const handleBulkTranslate = async () => {
    const targetLangs = supportedLanguages.filter(
      (code) => !isEnglishCode(code),
    );
    if (targetLangs.length === 0) {
      toast.warning(
        "No non-English languages configured for this organization",
      );
      return;
    }
    if (groups.length === 0) return;

    setBulkTranslating(true);
    try {
      const sourceStrings = groups.map((g) => ({ text: g.name }));

      const translationsByLang: Record<string, string[]> = {};
      for (const langCode of targetLangs) {
        const targetLocale = languageCodeToTranslateLocale(langCode);
        const res = await getSurveyTranslations({
          body: {
            source_strings: sourceStrings,
            target_language: targetLocale,
          },
        });
        translationsByLang[langCode] =
          res.data?.translations?.map(
            (item) => item.translations?.[0]?.text ?? "",
          ) ?? [];
      }

      const patchPromises = groups.map((group, groupIndex) => {
        const existing =
          group.translated_names && typeof group.translated_names === "object"
            ? (group.translated_names as Record<string, string>)
            : {};
        const merged = { ...existing };
        for (const langCode of targetLangs) {
          const translated = translationsByLang[langCode]?.[groupIndex];
          if (translated) {
            merged[langCode] = translated;
          }
        }
        return patchOrgSurveyGroup({
          path: { org_id: orgId ?? "", group_id: group.id },
          body: { translated_names: merged },
        });
      });

      const results = await Promise.all(patchPromises);
      setGroups((prev) =>
        prev.map((g) => {
          const updated = results.find((r) => r.data?.group.id === g.id);
          return updated?.data?.group ?? g;
        }),
      );
      toast.success(
        `Translated ${groups.length} group(s) to ${targetLangs.length} language(s)`,
      );
    } catch {
      toast.error("Bulk translation failed");
    } finally {
      setBulkTranslating(false);
    }
  };

  const nonEnglishLangs = supportedLanguages.filter(
    (code) => !isEnglishCode(code),
  );
  const showTranslateAll = nonEnglishLangs.length > 0 && groups.length > 0;

  const getConfigName = (id: string) =>
    configs.find((c) => c.id === id)?.name ?? id;

  return (
    <Box className={styles.root}>
      <Button
        onClick={() => navigate(`/${app}/${orgId}/forms`)}
        startIcon={<ArrowBack />}
        className={styles.backButton}
      >
        Back to Form Management
      </Button>

      <Grid container justifyContent="space-between" alignItems="center">
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="h4" gutterBottom>
            Survey Groups
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }} className={styles.actionsGrid}>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {showTranslateAll && (
              <Button
                variant="outlined"
                startIcon={
                  bulkTranslating ? (
                    <CircularProgress size={18} />
                  ) : (
                    <Translate />
                  )
                }
                onClick={handleBulkTranslate}
                disabled={bulkTranslating || loading}
              >
                {bulkTranslating ? "Translating..." : "Translate All"}
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateOpen(true)}
            >
              Add Group
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Typography
        variant="body1"
        color="text.secondary"
        className={styles.introText}
      >
        Groups organize survey configurations into categories shown on the
        public report selection page.
      </Typography>

      {!loading && groups.length === 0 && (
        <Typography color="text.secondary">
          {`No groups yet. Click "Add Group" to create one.`}
        </Typography>
      )}

      <Stack spacing={2}>
        {groups
          .slice()
          .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999))
          .map((group) => (
            <SurveyGroupCard
              key={group.id}
              group={group}
              getConfigName={getConfigName}
              onManage={() =>
                navigate(`/${app}/${orgId}/forms/groups/${group.id}`)
              }
              onDelete={() => setDeleteConfirmGroup(group)}
            />
          ))}
      </Stack>

      <CreateGroupDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
        orgId={orgId ?? ""}
      />

      <Dialog
        open={deleteConfirmGroup != null}
        onClose={() => setDeleteConfirmGroup(null)}
      >
        <DialogTitle>Delete Group</DialogTitle>
        <DialogContent>
          <Typography>
            {`Are you sure you want to delete "${deleteConfirmGroup?.name}"? This will remove all survey assignments from this group.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmGroup(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SurveyGroupManagement;
